import * as React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { CalendarIcon, Clock, Ellipsis, ShieldAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';
import { Badge } from '@/components/ui/badge';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { formatDate } from '@/lib/format';
import { BlockRule } from '@/lib/api/blockRules';

interface GetBlockRulesTableColumnsProps {
    onDelete: (ids: string[]) => void;
}

export function getBlockRulesTableColumns({ onDelete }: GetBlockRulesTableColumnsProps): ColumnDef<BlockRule>[] {
    return [
        {
            id: 'select',
            header: ({ table }) => (
                <Checkbox
                    checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                    className="translate-y-0.5"
                />
            ),
            cell: ({ row }) => <DataTableCheckbox row={row} />,
            enableSorting: false,
            enableHiding: false,
            size: 40,
        },
        {
            id: 'name',
            accessorKey: 'name',
            header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
            cell: ({ row }) => (
                <div className="flex items-center gap-2">
                    <ShieldAlert className="text-muted-foreground h-4 w-4 shrink-0" />
                    <span className="font-medium">{row.original.name}</span>
                </div>
            ),
            enableSorting: false,
            meta: { label: 'Name' },
        },
        {
            id: 'operator',
            accessorKey: 'operator',
            header: ({ column }) => <DataTableColumnHeader column={column} title="Operator" />,
            cell: ({ row }) => (
                <Badge variant="secondary" className="font-mono">
                    {row.original.operator}
                </Badge>
            ),
            enableSorting: false,
            meta: { label: 'Operator' },
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const { expiresAt, isPermanent } = row.original;
                if (!isPermanent && !expiresAt) {
                    return (
                        <Badge
                            variant="destructive"
                            title="No expiry and not permanent: this rule never blocks anything. Edit it to set one."
                        >
                            Inactive
                        </Badge>
                    );
                }
                const isActive = isPermanent || (expiresAt && new Date(expiresAt) > new Date());
                return (
                    <Badge variant={isActive ? 'green' : 'secondary'}>
                        {isActive ? 'Active' : 'Expired'}
                    </Badge>
                );
            },
            enableSorting: false,
        },
        {
            id: 'expiresAt',
            header: ({ column }) => <DataTableColumnHeader column={column} title="Expires At" />,
            cell: ({ row }) => {
                const { expiresAt, isPermanent } = row.original;
                if (isPermanent) {
                    return <span className="text-sm font-medium text-blue-600">Permanent</span>;
                }
                if (!expiresAt) {
                    return <span className="text-muted-foreground text-sm">—</span>;
                }
                const expired = new Date(expiresAt) <= new Date();
                return (
                    <div className="flex items-center gap-1.5">
                        <Clock className={`h-3.5 w-3.5 ${expired ? 'text-red-500' : 'text-muted-foreground'}`} />
                        <span className={`text-sm tabular-nums ${expired ? 'text-red-600' : ''}`}>
                            {formatDate(expiresAt, 'MM/dd/yyyy HH:mm')}
                        </span>
                    </div>
                );
            },
            enableSorting: false,
            meta: {
                label: 'Expires At',
                variant: 'dateRange',
                icon: () => <CalendarIcon />,
            },
        },
        {
            id: 'autoCreated',
            header: 'Source',
            cell: ({ row }) => (
                <Badge variant={row.original.autoCreated ? 'yellow' : 'outline'} className="text-xs">
                    {row.original.autoCreated ? 'Auto' : 'Manual'}
                </Badge>
            ),
            enableSorting: false,
        },
        {
            id: 'createdAt',
            accessorKey: 'createdAt',
            header: ({ column }) => <DataTableColumnHeader column={column} title="Created At" />,
            cell: ({ row }) => (
                <span className="text-sm tabular-nums">
                    {row.original.createdAt ? formatDate(row.original.createdAt, 'MM/dd/yyyy HH:mm') : '—'}
                </span>
            ),
            enableSorting: true,
            meta: { label: 'Created At' },
        },
        {
            id: 'id',
            accessorKey: 'id',
            header: ({ column }) => <DataTableColumnHeader column={column} title="Rule ID" />,
            cell: ({ row }) => {
                const id = row.original.id;
                return (
                    <CopyableResourceBadge
                        name="Rule ID"
                        value={`${id.substring(0, 8)}...`}
                        copyValue={id}
                    />
                );
            },
            enableSorting: false,
            enableHiding: true,
            size: 140,
            meta: { label: 'Rule ID' },
        },
        {
            id: 'actions',
            cell: ({ row }) => (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            aria-label="Open actions"
                            variant="ghost"
                            className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
                        >
                            <Ellipsis className="h-4 w-4" aria-hidden="true" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[160px]">
                        <DropdownMenuItem
                            className="text-red-600 focus:text-red-600"
                            onClick={() => onDelete([row.original.id])}
                        >
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            ),
            enableSorting: false,
            enableHiding: false,
            size: 40,
        },
    ];
}
